
import React from "react";
import ReactDOM from "react-dom/client";
import { i18nManager } from "../../../../i18n";
import { useSearchParams } from "react-router-dom";
import { Schemes, Subscriber, Utils } from "../../../../utils";
import { ConfirmDialog, Elements, SidedContainer, Siren } from "../../../shared";
import { Alignment, ButtonManageRef, Classname, FileHelper, FileInput, ObjectHelper, Scheme } from "@rivtn/noseur";
import { ApiResponseData, BaseService, ICountry, IProfile, CountryService, SanitizedResponse, CurrencyService, ICurrency } from "../../../../services";

function Countries() {
    const countryService = CountryService.getInstance();
    const currencyService = CurrencyService.getInstance();
    const dialogErrorRef = React.useRef<HTMLDivElement>(null);
    const [activeCountry, setActiveCountry] = React.useState<ICountry>();
    const labels = i18nManager.Labels.dashboard.ploutous.others.countries;
    const [searchParams, setSearchParams] = useSearchParams(window.location.search);
    const [countries, setCountries] = React.useState<ApiResponseData<ICountry>>({});

    React.useEffect(fetchCountries, []);

    return SidedContainer({
        searchParams,
        data: countries,
        setSearchParams,
        onAction: manageCountry,
        queryData: fetchCountries,
        labels: labels.sided_labels,
        activeData: (activeCountry ? {
            data: activeCountry,
            element: (<React.Fragment>
                <div className="header">
                    <i className="fa fa-times" onClick={() => {
                        setActiveCountry(undefined);
                        Utils.updateSearchParams("active_data_id", undefined, setSearchParams);
                    }} />
                    <span className="name">{activeCountry?.name}</span>
                    <span className="id">{activeCountry?.code} - {activeCountry?.short_name} - {activeCountry?.dial_code}</span>
                    <span className="id" style={{ fontWeight: "bold" }}>{activeCountry?.external_id}</span>
                    <span className="id"><span style={{ color: "#9E9E9E" }}>{labels.currency} - </span>{activeCountry?.currency?.name} ({activeCountry?.currency?.code})</span>
                </div>
                <div className="middle actions" style={{ marginBottom: 0 }}>
                    <span style={{ marginTop: 5 }} onClick={() => manageCountry(activeCountry)}><b>{i18nManager.Labels.dashboard.ploutous.created_by}:</b> {activeCountry?.created_by?.first_name} {activeCountry?.created_by?.last_name} ({activeCountry?.created_by?.email})</span>
                    <span style={{ marginTop: 5 }} onClick={() => manageCountry(activeCountry)}><b>{i18nManager.Labels.dashboard.ploutous.updated_by}:</b> {activeCountry?.updated_by?.first_name} {activeCountry?.updated_by?.last_name} ({activeCountry?.updated_by?.email})</span>
                    <span style={{ marginTop: 5 }} onClick={() => manageCountry(activeCountry)}><b>{i18nManager.Labels.dashboard.ploutous.created_at}:</b> {Utils.formatToFineDate5(activeCountry?.created_at)}</span>
                    <span style={{ marginTop: 5 }} onClick={() => manageCountry(activeCountry)}><b>{i18nManager.Labels.dashboard.ploutous.updated_at}:</b> {Utils.formatToFineDate5(activeCountry?.updated_at)}</span>
                </div>
                <div className="actions" style={{ overflow: "auto" }}>
                    <span className="link" onClick={() => manageCountry(activeCountry)}>{i18nManager.Labels.dashboard.ploutous.edit}</span>
                    {activeCountry?.status !== "ACTIVE" && (<span className="link" onClick={() => activateDeactivateCountry(activeCountry, true)}>{i18nManager.Labels.dashboard.ploutous.activate}</span>)}
                    {activeCountry?.status === "ACTIVE" && (<span className="link danger" onClick={() => activateDeactivateCountry(activeCountry, false)}>{i18nManager.Labels.dashboard.ploutous.deactivate}</span>)}
                    {activeCountry?.status !== "DELETED" && (<span className="link danger" onClick={() => deleteCountry(activeCountry)}>{i18nManager.Labels.dashboard.ploutous.delete}</span>)}
                </div>
            </React.Fragment>)
        } : undefined),
        dataTemplate: (country: ICountry) => {
            return (<div className={Classname.build("channel", (activeCountry?.external_id === country.external_id ? "active" : null))} onClick={() => {
                setActiveCountry(country);
                Utils.updateSearchParams("active_data_id", country.external_id, setSearchParams);
            }}>
                <div>
                    <div>
                        <i className="fa fa-edit link gray" style={{ marginRight: 10 }} onClick={() => manageCountry(country)} />
                        <i className={Classname.build("fa fa-trash link danger", (country.status === "DELETED" ? "noseur-disabled" : null))} style={{ marginRight: 10 }} onClick={() => deleteCountry(country)} />
                    </div>
                    <div>
                        <div className="id" style={country.status === "DELETED" ? { textDecoration: "line-through" } : {}}>
                            <span className="name">{country.name}</span>
                            <span className="type"> - {country.code}</span>
                        </div>
                        <span className="desc">{labels.dial_code}: {country.dial_code}</span>
                        <span className="desc" style={{ marginTop: 0 }}>{labels.short_name}: {country.short_name}</span>
                    </div>
                </div>
            </div>);
        }
    });

    function fetchCountries() {
        const id = searchParams.get("active_data_id");
        countryService.queryCountries(Utils.normalizeUrlParams(searchParams)).then(({ apiResponse }: SanitizedResponse<ICountry>) => {
            setCountries(apiResponse.data!);
            if (id) for (const item of apiResponse.data?.content!) {
                if (item.external_id === id) {
                    setActiveCountry(item);
                    break;
                }
            }
        }).catch(BaseService.reportError).finally(() => Subscriber.report(Subscriber.KEYS.QUERY_REQUEST_PROMISE_COMPLETED));
    }

    function manageCountry(country?: ICountry) {
        const payload = ObjectHelper.clone(country ?? {} as any);
        const basics = labels.dialog.manage.basic[country ? "update" : "create"];
        const dialog = ConfirmDialog({
            ...basics,
            closable: true,
            rightStyle: { flex: 1.5 },
            confirmLabel: basics.confirm_label,
            style: { minWidth: 400, maxWidth: "80%" },
            content: (<div className="add-new-role">
                <FileInput stickyPreview className="pic" rounded scheme={Schemes.RIVTN_QUIRINUS} accepts="image/*" style={{ maxWidth: 100 }}
                    control={<i className='fa fa-pen' />} attrsRelay={{ control: { style: { borderRadius: "50%" }, alignment: Alignment.BOTTOM_RIGHT } }}
                    emptyTemplate={(select) => <img onClick={select} style={{ width: "100%", height: "100%" }} src={country?.icon_png_base64} />} onSelectFiles={async ([ selectedFile, ..._ ]) => {
                        payload.icon_png_base64 = await FileHelper.fileToBase64(selectedFile)
                    }} />
                {Elements.buildConfirmDialogForm(labels.dialog.manage.form, (name: string, value: any, searching: any) => {
                    dialogErrorRef.current!.innerText = " ";
                    if (name === "dial_code") value = parseInt(value);
                    if (searching && name === "currency_id") {
                        currencyService.queryCurrencies({ name: value.value }).then(({ apiResponse }: SanitizedResponse<ICurrency>) => {
                            value.manageRef?.changeOptions(apiResponse.data!.content?.map((currency) => ({ value: currency.external_id, label: currency.name })));
                        }).catch(BaseService.reportError);
                    }
                    payload[name] = value;
                    return false;
                }, payload, undefined, { marginTop: 0 })}
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            onConfirm: (button?: ButtonManageRef) => {
                let errorMessage;
                dialogErrorRef.current!.innerText = " ";
                const formattedPayload = ObjectHelper.clone(payload);
                if (Object.keys(formattedPayload).length < 3) {
                    errorMessage = labels.dialog.manage.fill_required_fields;
                }
                if (errorMessage) {
                    dialogErrorRef.current!.innerText = errorMessage;
                    return false;
                }
                button?.setLoadingState(true);
                const method = (!!country ? countryService.updateCountry : countryService.createCountry).bind(countryService);
                method(formattedPayload)
                    .then(({ }: SanitizedResponse<ICountry>) => {
                        Siren.alert(basics.successful);
                        dialog.destroy(fetchCountries);
                    }).catch(({ errors }: SanitizedResponse<any>) => {
                        dialogErrorRef.current!.innerText = errors?.join("\n") ?? "";
                    }).finally(() => {
                        button?.setLoadingState(false);
                    });
                return false;
            }
        });
        dialog.show();
    }

    function activateDeactivateCountry(country: ICountry, activate: boolean) {
        countryService.updateCountry({ external_id: country.external_id, status: (activate ? "ACTIVE" : "IN_ACTIVE") }).then(() => {
            Siren.alert(activate ? labels.successfully_activate : labels.successfully_deactivate);
            fetchCountries();
        }).catch(BaseService.reportError);
    }

    function deleteCountry(country: ICountry) {
        const dialog = ConfirmDialog({
            closable: true,
            title: labels.dialog.delete.title,
            desc: ObjectHelper.expandStringTemplate(labels.dialog.delete.desc, { name_code: `${country.name} (${country.code})` }, { chop: "$" }),
            confirmScheme: Scheme.DANGER,
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                countryService.deleteCountry(country.external_id).then(() => {
                    Siren.alert(labels.dialog.delete.successful);
                    setActiveCountry(undefined);
                    dialog.destroy(() => {
                        fetchCountries();
                    });
                }).catch(BaseService.reportError).finally(() => button?.setLoadingState(false));
                return false;
            }
        });
        dialog.show();
    }

}

export default Countries;

